import { Button } from '@/components/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/dropdown';
import { FormLabel } from '@/components/form/formlabel';
import { Input } from '@/components/form/input';
import { Modal } from '@/components/modal';
import { DataTable } from '@/components/table/datatable';
import { idrFormatter } from '@/lib/utils/currency';
import { clearErrors } from '@/reducers/affiliators';
import { AppDispatch } from '@/root/store';
import {
  DeleteAffiliator,
  GetAffiliators,
  StoreAffiliator,
} from '@/services/affiliators';
import { ShowEvent } from '@/services/events';
import { NoSymbolIcon, PlusIcon, TrashIcon } from '@heroicons/react/24/outline';
import {
  ClipboardDocumentIcon,
  DocumentCheckIcon,
  EllipsisHorizontalIcon,
} from '@heroicons/react/24/solid';
import { ColumnDef } from '@tanstack/react-table';
import React, { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useParams } from 'react-router-dom';

interface Affiliator {
  id: number;
  name: string;
  email: string;
  referral_code: string;
  number_tickets_sold: number;
  total_transactions: number;
}

export function OrgEventAffiliator() {
  const location = useLocation();
  const { slug } = useParams();
  const dispatch: AppDispatch = useDispatch();
  const [page, setPage] = useState(1);
  const [showModal, setShowModal] = useState(false);
  const [showDelete, setShowDelete] = useState({
    modal: false,
    id: 0,
  });
  const [formData, setFormData] = useState({ name: '', email: '', limit: 0 });

  const { affiliators, affiliator, affiliatorDeleted, paginator, errors } =
    useSelector((state: any) => state.affiliators);
  const { event } = useSelector((state: any) => state.event);

  const queryParams = new URLSearchParams(location.search);
  const searchQuery: string = queryParams.get('search') ?? '';

  useEffect(() => {
    if (affiliator) {
      setShowModal(false);
    }

    dispatch(GetAffiliators(slug, searchQuery, page));
  }, [searchQuery, page, affiliator, affiliatorDeleted]);

  useEffect(() => {
    dispatch(ShowEvent(slug));
  }, [slug]);

  const setData = (e: any, element: string) => {
    setFormData({ ...formData, [element]: e.target.value });

    dispatch(clearErrors());
  };

  const submitAffiliator = () => {
    dispatch(StoreAffiliator(event.id, formData));
  };

  const deleteAffiliator = (affiliatorId: number) => {
    dispatch(DeleteAffiliator(event.id, affiliatorId));

    setShowDelete({ modal: false, id: 0 });
  };

  const copyAffiliatorLink = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);

      toast.success('Berhasil menyalink link');
    } catch (err) {
      toast.error('Gagal menyalin link');
    }
  };

  const ctaButton = (
    <Button
      onClick={() => {
        setShowModal(true);
        setFormData({ name: '', email: '', limit: 0 });
      }}
      leftIcon={<PlusIcon />}
    >
      Tambah affiliator
    </Button>
  );

  const columns: ColumnDef<Affiliator>[] = [
    {
      header: 'Affiliator',
      cell: ({ row }) => {
        const name = row.original.name;
        const email = row.original.email;
        return (
          <div>
            <p>{name}</p>
            <span className='text-gray-500'>{email}</span>
          </div>
        );
      },
    },
    {
      header: 'Kode referal',
      accessorKey: 'referral_code',
    },
    {
      header: 'Tiket terjual',
      accessorKey: 'number_tickets_sold',
    },
    {
      header: 'Limit transaksi',
      accessorKey: 'limit',
    },
    {
      header: 'Nominal transaksi',
      cell: ({ row }) => {
        const totalSold = row.original.total_transactions;
        return (
          <span className='text-danger'>{idrFormatter.format(totalSold)}</span>
        );
      },
    },
    {
      header: 'Aksi',
      cell: ({ row }) => {
        return (
          <DropdownMenu>
            <DropdownMenuTrigger>
              <Button
                size='sm'
                variant='neutral'
                leftIcon={<EllipsisHorizontalIcon />}
              />
            </DropdownMenuTrigger>
            <DropdownMenuContent>
              <DropdownMenuItem
                onClick={() => {
                  copyAffiliatorLink(
                    `${process.env.REACT_APP_CUSTOMER_URL}/${event.slug}?referral_code=${row.original.referral_code}`
                  );
                }}
              >
                <ClipboardDocumentIcon className='mr-2 h-4 w-4' />
                <span>Salin Link</span>
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={() => {
                  setShowDelete({ modal: true, id: row.original.id });
                }}
              >
                <TrashIcon className='mr-2 h-4 w-4 text-danger' />
                <span className='text-danger'>Hapus</span>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        );
      },
    },
  ];

  return (
    <>
      <Modal
        show={showModal}
        onRequestClose={() => setShowModal(false)}
        width='medium'
        title='Tambah Affiliator'
        body={
          <div className='flex flex-col gap-4'>
            <div className='mb-3'>
              <FormLabel label='Nama' errorText={errors.name} required>
                <Input
                  type='text'
                  value={formData.name}
                  onChange={(e) => setData(e, 'name')}
                />
              </FormLabel>
            </div>
            <div className='mb-3'>
              <FormLabel label='Email' errorText={errors.email} required>
                <Input
                  type='email'
                  value={formData.email}
                  onChange={(e) => setData(e, 'email')}
                />
              </FormLabel>
            </div>
            <div className='mb-3'>
              <FormLabel
                label='Limit transaksi'
                errorText={errors.limit}
                helperText='Biarkan 0 jika tidak ingin membatasi penjualan'
                required
              >
                <Input
                  type='number'
                  value={formData.limit}
                  onChange={(e) => setData(e, 'limit')}
                />
              </FormLabel>
            </div>
          </div>
        }
        footer={
          <div className='flex justify-end gap-3'>
            <Button
              variant='neutral'
              onClick={() => setShowModal(false)}
              leftIcon={<NoSymbolIcon />}
            >
              Batal
            </Button>
            <Button onClick={submitAffiliator} leftIcon={<DocumentCheckIcon />}>
              Simpan
            </Button>
          </div>
        }
      />

      <Modal
        show={showDelete.modal}
        onRequestClose={() => setShowDelete({ ...showDelete, modal: false })}
        width='medium'
        title='Hapus Affiliator'
        body={
          <>
            <p>Apakah kamu yakin ingin menghapus affiliator ini?</p>
          </>
        }
        footer={
          <div className='flex justify-end gap-3'>
            <Button
              variant='neutral'
              onClick={() => setShowDelete({ ...showDelete, modal: false })}
              leftIcon={<NoSymbolIcon />}
            >
              Batal
            </Button>
            <Button
              variant={'danger'}
              onClick={() => {
                deleteAffiliator(showDelete.id);
              }}
              leftIcon={<TrashIcon />}
            >
              Ya, Hapus
            </Button>
          </div>
        }
      />

      <DataTable
        name='Daftar affiliator'
        noun={'affiliator'}
        ctaButton={
          !['finance', 'scanner'].includes(event?.logged_in_organizer?.role) ? (
            ctaButton
          ) : (
            <></>
          )
        }
        columns={columns}
        data={affiliators}
        total={paginator.to}
        currentTotal={paginator.total}
        currentPage={paginator.current_page}
        pages={paginator.last_page}
        onPageRequest={(nextPage) => setPage(nextPage)}
      />
    </>
  );
}
